import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import LegalFootnote from './components/LegalFootnote';
import './portal-v2.css';

const PortalV2Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, forgotPassword } = useAuth();

  useEffect(() => { document.title = 'Sign In | WesTex Legal Portal'; }, []);

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const rawFrom = location.state?.from?.pathname || '';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await login(formData.email, formData.password);
    if (result.success) {
      // Choose the post-login destination based on the user's role —
      // never let a non-admin land on an admin route they were redirected from.
      let dest;
      if (result.user.is_admin) {
        dest = rawFrom.startsWith('/portal/admin') ? rawFrom : '/portal/admin';
      } else {
        dest = (rawFrom && !rawFrom.startsWith('/portal/admin') && rawFrom !== '/portal/login')
          ? rawFrom
          : '/portal/dashboard';
      }
      navigate(dest, { replace: true });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setForgotSuccess('');
    const result = await forgotPassword(forgotEmail);
    if (result.success) setForgotSuccess(result.message);
    else setError(result.error);
    setLoading(false);
  };

  return (
    <div className="portal-v2">
      <div className="pv2-login-shell">
        <div className="pv2-login-bg" style={{ backgroundImage: 'url(/lubbock-skyline.jpg)' }} aria-hidden="true" />
        <div className="pv2-login-card" data-testid="pv2-login-card">
          <div className="pv2-mono-tag" style={{ marginBottom: 14 }}>WesTex Legal Secure Portal</div>
          <h1 className="pv2-display" style={{ fontSize: 40, marginBottom: 8 }}>
            {forgotMode ? 'Reset ' : 'Sign '}
            <span className="pv2-display-italic">{forgotMode ? 'your password.' : 'in.'}</span>
          </h1>
          <p style={{ color: 'var(--pv2-text-muted)', fontSize: 13, marginBottom: 28 }}>
            {forgotMode
              ? 'Enter the email associated with your account.'
              : 'Access jobs, invoices, and case progress in real time.'}
          </p>

          {error && (
            <div className="pv2-alert pv2-alert-red" data-testid="pv2-login-error">
              <AlertCircle size={16} strokeWidth={1.5} />
              <span>{error}</span>
            </div>
          )}
          {forgotSuccess && (
            <div className="pv2-alert pv2-alert-green">
              <CheckCircle size={16} strokeWidth={1.5} />
              <span>{forgotSuccess}</span>
            </div>
          )}

          {!forgotMode && (
            <form onSubmit={handleSubmit} data-testid="pv2-login-form">
              <div className="pv2-field">
                <label className="pv2-label">Email</label>
                <input
                  className="pv2-input"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  placeholder="you@lawfirm.com"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="pv2-login-email"
                />
              </div>
              <div className="pv2-field">
                <label className="pv2-label">Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className="pv2-input"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    style={{ paddingRight: 44 }}
                    data-testid="pv2-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    aria-label="Toggle password"
                    style={{
                      position: 'absolute', top: 0, right: 0, height: '100%',
                      width: 44, background: 'transparent', border: 0,
                      color: 'var(--pv2-text-muted)', cursor: 'pointer',
                    }}
                  >
                    {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} className="pv2-btn pv2-btn-primary" style={{ width: '100%' }} data-testid="pv2-login-submit">
                {loading ? 'Signing in…' : 'Sign In'}
              </button>

              <LegalFootnote action="signing in" testId="pv2-login-legal" />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20, fontSize: 12 }}>
                <button type="button" onClick={() => setForgotMode(true)} className="pv2-mono" style={{ background: 'none', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer', padding: 0 }}>
                  Forgot password
                </button>
                <Link to="/portal/register" className="pv2-mono" style={{ color: 'var(--pv2-red-bright)' }}>
                  Create account
                </Link>
              </div>
            </form>
          )}

          {forgotMode && (
            <form onSubmit={handleForgot} data-testid="pv2-forgot-form">
              <div className="pv2-field">
                <label className="pv2-label">Email</label>
                <input
                  className="pv2-input"
                  type="email"
                  required
                  placeholder="you@lawfirm.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  data-testid="pv2-forgot-email"
                />
              </div>
              <button type="submit" disabled={loading} className="pv2-btn pv2-btn-primary" style={{ width: '100%' }} data-testid="pv2-forgot-submit">
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
              <button
                type="button"
                onClick={() => { setForgotMode(false); setError(''); setForgotSuccess(''); }}
                className="pv2-mono"
                style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18, background: 'none', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer', padding: 0 }}
              >
                <ArrowLeft size={12} /> Back to sign in
              </button>
            </form>
          )}

          <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid var(--pv2-ink-line)' }}>
            <Link to="/" className="pv2-mono-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--pv2-text-subtle)' }}>
              <ArrowLeft size={12} /> Back to website
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalV2Login;
